import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  children: ReactNode;
}

// Thin React wrapper over the `.btn` CSS system (see globals.css) so there is a
// SINGLE source of truth for button styling. Don't hardcode dimensions/colors
// here — edit the `.btn*` rules in globals.css and both the class-based and
// component-based call sites stay in sync.
const SIZE_CLASS: Record<ButtonSize, string> = { sm: 'btn-sm', md: '', lg: 'btn-lg' };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      icon,
      children,
      className = '',
      type,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        // Not a submit button unless asked: a <Button> inside a form used to submit it.
        type={type ?? 'button'}
        disabled={isDisabled}
        className={['btn', `btn-${variant}`, SIZE_CLASS[size], className].filter(Boolean).join(' ')}
        {...props}
      >
        {loading ? (
          <Loader2
            className="animate-spin shrink-0"
            size={size === 'sm' ? 14 : 16}
            aria-label={t('common.loading')}
          />
        ) : icon ? (
          <span className="shrink-0 inline-flex" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
